<template>
  <Card cardClasses="shadow-sm border-0">
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h4 class="m-0 text-primary">
        <i class="bi bi-file-earmark-text me-2"></i>Z-Reports
      </h4>
    </div>

    <div class="container-fluid p-0">
      <div v-if="loading" class="text-center py-5">
        <Spinner spinner-class="mb-1" />
        <p class="text-muted mt-2">Loading Z-reports...</p>
      </div>

      <div v-if="!loading" class="zreports-content fade-in">
        <div class="d-flex flex-wrap align-items-center justify-content-between mb-3">
          <div class="d-flex align-items-center mb-2 mb-sm-0">
            <span class="badge bg-primary rounded-pill me-2">{{ zreports.total }}</span>
            <span class="text-muted">Z-Reports</span>
          </div>
          <div class="d-flex gap-2 align-items-center flex-wrap">
            <select class="form-select w-auto" v-model="filterStoreId" @change="onFilterChange">
              <option value="">All Stores</option>
              <option v-for="store in stores" :key="store.id" :value="store.id">{{ store.name }}</option>
            </select>
            <select class="form-select w-auto" v-model="filterUserId" @change="onFilterChange">
              <option value="">All Users</option>
              <option v-for="user in users" :key="user.id" :value="user.id">{{ user.first_name }} {{ user.last_name }}</option>
            </select>
            <input 
              type="date" 
              class="form-control w-auto" 
              v-model="filterFromDate" 
              @change="onFilterChange"
              placeholder="From Date">
            <input 
              type="date" 
              class="form-control w-auto" 
              v-model="filterToDate" 
              @change="onFilterChange"
              placeholder="To Date">
            <UInput v-model="dataTableSearch" size="md" placeholder="Search reports" class="mb-0 w-auto" />
            <button class="btn btn-primary" @click="onSearchClick">
              <i class="bi bi-search me-1"></i> Search
            </button>
            <button class="btn btn-outline-secondary" @click="onClearSearchClick" :disabled="!dataTableSearch || !dataTableSearch.length">
              <i class="bi bi-x-circle me-1"></i> Clear
            </button>
          </div>
        </div>

        <div class="card border-0 shadow-sm mb-4">
          <div class="table-responsive">
            <table class="table table-hover align-middle mb-0">
              <thead>
                <tr>
                  <th>REFERENCE</th>
                  <th>SHIFT REF</th>
                  <th>STORE</th>
                  <th>USER</th>
                  <th>TRANSACTIONS</th>
                  <th>TOTAL SALES</th>
                  <th>CASH VARIANCE</th>
                  <th>GENERATED AT</th>
                  <th>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(report, index) in zreports.data" :key="report.id" class="report-row" :class="{'bg-light': index % 2 === 0}">
                  <td>{{ report.reference }}</td>
                  <td>{{ report.shift?.reference || 'N/A' }}</td>
                  <td>{{ report.store?.name || 'N/A' }}</td>
                  <td>{{ report.user?.name || 'N/A' }}</td>
                  <td>{{ report.total_transactions }}</td>
                  <td>{{ formatNumber(report.total_sales) }}</td>
                  <td>
                    <span :class="getCashDifferenceClass(report.cash_difference)">
                      {{ 'KES ' + report.cash_difference }}
                    </span>
                  </td>
                  <td>{{ formatDate(report.generated_at) }}</td>
                  <td>
                    <button class="btn btn-info btn-sm" @click="viewReportDetails(report.id)">
                      <i class="bi bi-eye me-1"></i> View
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="d-flex flex-wrap pt-3 pb-4 align-items-end mx-4 justify-content-between">
          <div class="me-sm-4 mb-sm-0 mb-3">
            <p class="mb-2 small">Rows per page</p>
            <select class="form-select" v-model.number="dataTablePerPage">
              <option v-for="item in [5,10,25,50,100]" :key="item" :value="item">{{item}}</option>
            </select>
          </div>
          <div>
            <Pagination
              v-model="dataTablePage"
              :pages="totalPages"
              :threshold="2">
              <template #previous>Prev</template>
              <template #next>Next</template>
            </Pagination>
          </div>
        </div>

        <div class="d-flex justify-content-center">
          <button class="btn btn-outline-secondary" @click="$router.go(-1)">
            <i class="bi bi-arrow-left me-1"></i> Back
          </button>
        </div>
      </div>
    </div>
  </Card>
</template>

<script>
import zreportService from "@/api/zreportService"
import storeService from "@/api/storeService"
import userService from "@/api/userService"
import router from "@/router"
import {decrypt} from "@/utils/crypto"
import formatDate from "@/utils/formatDate"
import formatNumber from "@/utils/formatNumber"

export default {
  name: "ZReports",

  data(){
    this.profile = JSON.parse(decrypt(sessionStorage.getItem('profile')))
    this.store = JSON.parse(decrypt(sessionStorage.getItem('store')))
    this.organization = JSON.parse(decrypt(sessionStorage.getItem('organization')))
    return {
      organization: {},
      store: {},
      profile: {},
      loading: true,
      zreports: {
        data: []
      },
      dataTableSearch: this.$route?.query?.search ?? '',
      dataTablePage: this.$route?.query?.page ? parseInt(this.$route.query.page) : 1,
      dataTablePerPage: this.$route?.query?.per_page ? parseInt(this.$route.query.per_page) : 10,
      filterFromDate: this.$route?.query?.from_date ?? '',
      filterToDate: this.$route?.query?.to_date ?? '',
      filterStoreId: this.$route?.query?.store_id ?? '',
      filterUserId: this.$route?.query?.user_id ?? '',
      stores: [],
      users: [],
    }
  },

  methods:{
    formatDate,
    formatNumber,

    async fetchZReports() {
      this.loading = true
      const params = {
        page: this.dataTablePage,
        per_page: this.dataTablePerPage,
        search: this.dataTableSearch,
        from_date: this.filterFromDate,
        to_date: this.filterToDate,
        store_id: this.filterStoreId,
        user_id: this.filterUserId
      }
      this.zreports = await zreportService.getZReports(params)
      this.loading = false
    },

    async fetchStores() {
      this.stores = await storeService.getStores()
    },

    async fetchUsers() {
      const response = await userService.getUsers({ per_page: 100 })
      this.users = response.data || []
    },

    onSearchClick() {
      this.dataTablePage = 1
      this.updateQueryParams()
      this.fetchZReports()
    },

    onClearSearchClick() {
      this.dataTableSearch = ''
      this.filterFromDate = ''
      this.filterToDate = ''
      this.filterStoreId = ''
      this.filterUserId = ''
      this.dataTablePage = 1
      this.updateQueryParams()
      this.fetchZReports()
    },

    onFilterChange() {
      this.dataTablePage = 1
      this.updateQueryParams()
      this.fetchZReports()
    },

    updateQueryParams() {
      const query = {}
      if (this.dataTablePage > 1) query.page = this.dataTablePage
      if (this.dataTablePerPage !== 10) query.per_page = this.dataTablePerPage
      if (this.dataTableSearch) query.search = this.dataTableSearch
      if (this.filterFromDate) query.from_date = this.filterFromDate
      if (this.filterToDate) query.to_date = this.filterToDate
      if (this.filterStoreId) query.store_id = this.filterStoreId
      if (this.filterUserId) query.user_id = this.filterUserId
      router.push({ query })
    },

    viewReportDetails(id) {
      router.push('/z-report/' + id)
    },

    getCashDifferenceClass(difference) {
      const diff = parseFloat(difference)
      if (diff > 0) return 'text-success fw-bold'
      if (diff < 0) return 'text-danger fw-bold'
      return 'text-muted'
    },
  },

  computed: {
    totalPages() {
      return Math.ceil(this.zreports.total / this.dataTablePerPage)
    }
  },

  watch: {
    dataTablePage() {
      this.updateQueryParams()
      this.fetchZReports()
    },
    dataTablePerPage() {
      this.dataTablePage = 1
      this.updateQueryParams()
      this.fetchZReports()
    }
  },

  async mounted() {
    await this.fetchStores()
    await this.fetchUsers()
    await this.fetchZReports()
  }
}
</script>

<style scoped>
.zreports-content {
  padding: 0.5rem;
}

.table th {
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.7rem;
  letter-spacing: 0.5px;
  color: #6b7280;
  border-top: none;
  padding: 1rem 1.5rem;
  background-color: #f9fafb;
  border-bottom: 1px solid #e9ecef;
}

.table td {
  padding: 1rem 1.5rem;
  vertical-align: middle;
  border-color: #e9ecef;
  color: #4b5563;
}

.report-row {
  transition: all 0.2s ease;
}

.report-row:hover {
  background-color: #f8f9fa;
  transform: translateY(-2px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
}

.badge {
  font-weight: 500;
  padding: 0.35em 0.65em;
  font-size: 0.75em;
  border-radius: 6px;
}

.fade-in {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
